import styled from 'styled-components';
import {
  DropDown,
  Flex,
  FormField,
  InfoTooltip,
  Radio,
} from '@elementary/common-ui/components';
import { pluralizeString } from '@elementary/common-ui/utils/common';
import {
  FormControlLabel as FormControlLabelBase,
  RadioGroup,
  Typography,
} from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';
import { RuleFormFields } from 'shared/types';
import { fontSize, space } from '@elementary/common-ui/style/utils';

const FormControlLabel = styled(FormControlLabelBase)`
  gap: ${space('sm')};

  .MuiFormControlLabel-label {
    font-size: ${fontSize('base')};
  }
`;

const SelectRow = styled(Flex)`
  align-items: center;
  gap: ${space('md')};
  flex-wrap: wrap;
  margin-top: ${space('sm')};
`;

const LOOKBACK_DAYS = [1, 3, 7, 14, 30];

const DigestLookbackFormContent = () => {
  const methods = useFormContext<RuleFormFields>();
  const daysBack = methods.watch('daysBack');
  const isCurrent = daysBack != null;

  return (
    <FormField title="What to include" sx={{ mt: 3 }}>
      <Controller
        control={methods.control}
        name="daysBack"
        render={({ field }) => (
          <>
            <RadioGroup
              value={field.value == null ? 'incremental' : 'current'}
              sx={{ gap: 2 }}
              onChange={(_, value) => {
                if (value === 'incremental') {
                  field.onChange(null);
                } else if (value === 'current' && field.value == null) {
                  field.onChange(3);
                }
              }}
            >
              <FormControlLabel
                sx={{ m: 0 }}
                value="incremental"
                control={<Radio sx={{ paddingY: 0, pl: '9px', pr: '2px' }} />}
                label={
                  <Flex sx={{ alignItems: 'center', gap: 0.5 }}>
                    Only what changed
                    <InfoTooltip
                      size={16}
                      title="Only incidents that opened, were acknowledged, or resolved since the previous report."
                    />
                  </Flex>
                }
              />
              <FormControlLabel
                sx={{ m: 0 }}
                value="current"
                control={<Radio sx={{ paddingY: 0, pl: '9px', pr: '2px' }} />}
                label={
                  <Flex sx={{ alignItems: 'center', gap: 0.5 }}>
                    Full incident status
                    <InfoTooltip
                      size={16}
                      title="All active incidents in the look-back window, and everything that changed since the last report."
                    />
                  </Flex>
                }
              />
            </RadioGroup>

            {isCurrent && (
              <>
                <SelectRow>
                  <Typography
                    sx={{
                      fontSize: fontSize('base'),
                      color: 'content.secondary',
                    }}
                  >
                    Look back
                  </Typography>
                  <DropDown
                    value={String(field.value ?? 3)}
                    onChange={(value) => field.onChange(Number(value))}
                    options={LOOKBACK_DAYS.map((days) => ({
                      value: String(days),
                      label: pluralizeString(days, 'day', 'days'),
                    }))}
                    showClear={false}
                    sortOptions={false}
                    sx={{ minWidth: 120 }}
                  />
                </SelectRow>
                <Typography
                  sx={{
                    mt: 1,
                    fontSize: fontSize('sm'),
                    color: 'content.secondary',
                  }}
                >
                  Shows open/acknowledged incidents whose last failure is within
                  this window.
                </Typography>
              </>
            )}
          </>
        )}
      />
    </FormField>
  );
};

export default DigestLookbackFormContent;
